import { ProductVariantV3 } from '@dehaat/kisan-app-bl/models/ProductV3';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import { MouseEventHandler, useContext, useState } from 'react';
import { CART_TYPE } from '@/constants/cart';
import { AVAILABLE_STOCK } from '@/constants/common';
import { CartContext } from '@/context/CartProvider';
import { UserContext } from '@/context/UserProvider';
import { useAppSelector } from '@/hooks/reduxHooks';
import useTriggerEvents from '@/hooks/useTriggerEvents';
import { ProductVariant } from '@/models/Product';
import { selectCurrentVendor } from '@/store/features/locationSlice';
import CustomEventTypes from '@/utils/analytics/customEventTypes';
import { ProductListTypeEnum } from '@/utils/analytics/types';
import addWishlist from '@/utils/api/addWishlist';
import { isShriProgramProduct, productCatalogueId } from '@/utils/cart';
import { getCookieValue } from '@/utils/helper';
import { getCartUpdatePricing } from '@/utils/helpers/flashSale';
import AddRemoveButton from '../AddRemoveButton';
import ButtonLabel, { VARIANT } from '../ButtonLabel';
import PlusIcon from '../icons/PlusIcon';
import MoveToWishlist from '../MoveToWishlist';
import { requiredPathName } from './AddToCart2';
interface Props {
  btnContainerStyle?: string;
  index?: number;
  listType?: ProductListTypeEnum;
  minQty?: number;
  productVariant: ProductVariant | ProductVariantV3;
  maxQty?: number;
  productId?: number;
  productName?: string;
  productBrand?: string;
  productCategory?: string;
}
const AddToCartNew = ({
  btnContainerStyle,
  index,
  listType,
  minQty,
  productVariant,
  productId,
  productName,
  productBrand,
  productCategory
}: Props) => {
  const {
    t
  } = useTranslation('products');
  const {
    trackCustomEvent
  } = useTriggerEvents();
  const vendorDetails = useAppSelector(selectCurrentVendor);
  const router = useRouter();
  const userId = getCookieValue('user_id');
  const {
    cartItems,
    updateCart,
    cartType
  } = useContext(CartContext);
  const stopBubbling: MouseEventHandler<HTMLElement> = e => {
    e.preventDefault();
    e.stopPropagation();
  };
  const [showMoveToWishlistModal, setShowMoveToWishlistModal] = useState(false);
  const StockInSS = typeof window !== 'undefined' && sessionStorage?.getItem(AVAILABLE_STOCK);
  const availableStock = StockInSS ? JSON.parse(StockInSS) : undefined;
  const quantity = cartItems[productVariant.id] || 0;
  const disableRemove = !(typeof minQty === 'undefined' || quantity > minQty);
  const disableEditQuantity = isShriProgramProduct(productVariant.id, false);
  const diasbleAdd = availableStock && availableStock[productVariant.id] !== 'undefined' && quantity >= availableStock[productVariant.id];
  const onAddOrRemoveFromCart = (isAdd: boolean) => {
    let name, brandName;
    if (cartType === CART_TYPE.DEHAAT) {
      name = (productVariant as ProductVariant).product?.name;
      brandName = (productVariant as ProductVariant).product?.brand?.name;
    } else {
      name = (productVariant as ProductVariantV3).name;
      brandName = (productVariant as ProductVariantV3).brand.name;
    }
    if (sessionStorage.getItem(AVAILABLE_STOCK)) {
      sessionStorage.removeItem(AVAILABLE_STOCK);
    }
    const pricing = getCartUpdatePricing(productVariant as ProductVariant);
    updateCart({
      variantId: productVariant.id,
      quantity: 1,
      index,
      type: listType,
      ...pricing,
      name,
      brandName,
      catalogueId: productCatalogueId(productVariant.inventory_set),
      groupId: {
        parent_id: (productVariant as ProductVariant).product?.parent_id,
        parent_type: (productVariant as ProductVariant).product?.parent_type
      }
    }, isAdd);
  };
  const handlerOnMoveToWishlist = async () => {
    try {
      await addWishlist({
        userId: userId,
        productId: productId!
      });
    } catch (e) {
      console.log('fail to add in wishlist: ', e);
    } finally {
      onAddOrRemoveFromCart(false);
      const notifyEventProps = {
        VendorName: vendorDetails?.company_name,
        VendorId: vendorDetails?.id,
        ProductName: productName,
        ProductBrand: productBrand,
        ProductCategory: productCategory,
        PageLocation: router.asPath,
        PageName: requiredPathName(router.asPath)
      };
      trackCustomEvent(CustomEventTypes.USER_CLICKED_NOTIFY_ME_CTA, JSON.stringify(notifyEventProps));
    }
  };
  return <div data-sentry-component="AddToCartNew" data-sentry-source-file="AddToCartNew.tsx">
      {quantity === 0 ? <button className="flex justify-center items-center bg-primary-100 py-2.5 rounded-lg min-w-[110px]" onClick={e => {
      onAddOrRemoveFromCart(true);
      const eventParam = {
        ProductName: (productVariant as ProductVariant).product?.name,
        ProductVariant: (productVariant as ProductVariant).product?.id,
        ProductBrand: (productVariant as ProductVariant).product?.brand?.name,
        ProductPrice: (productVariant as ProductVariant).inventory_set[0].price,
        Page: router.asPath,
        VendorName: vendorDetails?.company_name,
        VendorId: vendorDetails?.id
      };
      if (eventParam.VendorId && eventParam.VendorName) {
        trackCustomEvent(CustomEventTypes.ADD_TO_CART, JSON.stringify(eventParam));
      }
      stopBubbling(e);
    }}>
          <PlusIcon height={20} width={20} className="fill-transparent stroke-white" />
          <ButtonLabel variant={VARIANT.TWO} className="ml-2 font-nato-semibold text-white" label={t('add')} />
        </button> : <AddRemoveButton btnContainerStyle={disableEditQuantity ? '' : btnContainerStyle} disableRemove={disableRemove || disableEditQuantity} disableAdd={diasbleAdd || disableEditQuantity} onAdd={() => {
      trackCustomEvent(CustomEventTypes.ADD_ONE_ITEM_TO_CART);
      onAddOrRemoveFromCart(true);
    }} onRemove={() => {
      trackCustomEvent(CustomEventTypes.REMOVE_ONE_ITEM_FROM_CART);
      if (!disableRemove) {
        {
          quantity === 1 ? setShowMoveToWishlistModal(true) : onAddOrRemoveFromCart(false);
        }
      }
    }} quantity={quantity} />}
      <MoveToWishlist isOpen={showMoveToWishlistModal} onClose={() => {
      setShowMoveToWishlistModal(false);
    }} onMoveToWishlist={handlerOnMoveToWishlist} onRemoveFromCart={() => onAddOrRemoveFromCart(false)} data-sentry-element="MoveToWishlist" data-sentry-source-file="AddToCartNew.tsx" />
    </div>;
};
export default AddToCartNew;